/* The editable example permit + "Try a result" beside a Permit Row Colors Preview grid — ONE
 * component, shared by the list page and the New / Edit page, so both validate and evaluate alike.
 *
 * The boxes are the limit types the previewed rule(s) name (`fields`, from exampleFieldsFor) — the
 * rule ticked on the list, or the rule being edited — never a fixed set (owner request 2026-10-23).
 * Every keystroke re-evaluates through the shared engine (`permitRowRule`) with the rules the
 * caller passes, so the result chip and the Preview grid (which the caller colours from the same
 * `example`) always agree. Invalid input shows an inline message (BO-UI §13 tier 3) and is treated
 * as empty; nothing is saved, reset or closed. */
import { permitRowRule } from '../../analysis/permitLimit';
import type { RowColorRule } from '../../analysis/permitRowColorState';
import {
  EXAMPLE_UNITS, exampleLimits, exampleMdl, exampleText, limitError, resultError,
  type ExampleField, type ExamplePermit,
} from './permitExample';

export interface ExamplePermitControlsProps {
  /** The rules to evaluate against, in their priority order. */
  rules: RowColorRule[];
  /** The limit boxes to show — the limit types those rules name. */
  fields: ExampleField[];
  example: ExamplePermit;
  onExampleChange: (next: ExamplePermit) => void;
  tryValue: string;
  onTryChange: (next: string) => void;
  /** Stack one box per row — for a column BESIDE the grid rather than a row under it. */
  vertical?: boolean;
}

export function ExamplePermitControls({
  rules, fields, example, onExampleChange, tryValue, onTryChange, vertical,
}: ExamplePermitControlsProps) {
  const errors = fields
    .map((f) => ({ key: f.key, message: limitError(f.label, exampleText(example, f.key)) }))
    .filter((e): e is { key: string; message: string } => !!e.message);
  const tryError = resultError(tryValue);
  const hasResult = tryValue.trim() !== '' && !tryError;
  const tryRule = hasResult
    ? permitRowRule(rules, exampleLimits(example, fields), tryValue.trim(), EXAMPLE_UNITS, exampleMdl(example, fields))
    : undefined;
  const typed = Object.keys(example).length > 0;

  return (
    <div className={`prc-ex${vertical ? ' prc-ex--vertical' : ''}`}>
      {/* Not saved anywhere — data-unsaved-ignore keeps these boxes out of the "unsaved changes" guard. */}
      <div className="prc-example" role="group" aria-label="Example permit">
        <span className="sd-label">Example permit ({EXAMPLE_UNITS}):</span>
        {fields.map((f) => {
          const invalid = errors.some((e) => e.key === f.key);
          return (
            <label className="prc-ex-field" key={f.key}>
              <span className="sd-label">{f.label}{f.op ? ` ${f.op}` : ''}</span>
              <input
                className={`sd-input prc-ex-input${invalid ? ' prc-invalid' : ''}`}
                inputMode="decimal" value={exampleText(example, f.key)} aria-label={`Example ${f.label}`}
                aria-invalid={invalid || undefined} data-unsaved-ignore
                onChange={(e) => onExampleChange({ ...example, [f.key]: e.target.value })}
              />
            </label>
          );
        })}
        <button type="button" className="ca-btn prc-ex-reset" aria-label="Reset"
          title="Reset — back to the starting example values"
          disabled={!typed} onClick={() => onExampleChange({})}>
          <span className="tb-i tb-i-grey">↺</span>
        </button>
      </div>
      {fields.length === 0 && (
        <div className="prc-muted prc-ex-none">The rule names no limit type yet.</div>
      )}
      {errors.length > 0 && (
        <div className="prc-ex-errors" role="alert">
          {errors.map((e) => <span key={e.key} className="sd-field-error">{e.message}</span>)}
        </div>
      )}

      <div className="prc-try">
        <label htmlFor="prc-try-input" className="sd-label">Try a result:</label>
        <input id="prc-try-input" className={`sd-input prc-try-input${tryError ? ' prc-invalid' : ''}`}
          inputMode="decimal" value={tryValue} aria-invalid={tryError ? true : undefined} data-unsaved-ignore
          onChange={(e) => onTryChange(e.target.value)} />
        <span className="prc-muted">{EXAMPLE_UNITS}</span>
        <span className="prc-try-res" style={tryRule ? { background: tryRule.color } : undefined} aria-live="polite">
          {!hasResult ? '—' : tryRule ? tryRule.name : 'Normal colour'}
        </span>
      </div>
      {tryError && (
        <div className="prc-ex-errors" role="alert"><span className="sd-field-error">{tryError}</span></div>
      )}
    </div>
  );
}
